import type {
  CopilotAttribution,
  CopilotConversationSummary,
  KnowledgeScope,
} from "@yazamco/shared-types";

/**
 * Where the chat was aimed, for the transcript header.
 *
 * A scoped chat and an unscoped one produce answers that look alike on the
 * page, and the difference is exactly what a reader of an exported transcript
 * needs in order to judge it: "Copilot found nothing" means one thing across
 * all of Microsoft 365 and something much narrower inside one folder. The
 * `label` is carried alongside the scope because rendering a `SiteID` or a
 * `Path` back into the name the user picked belongs to the picker, not here.
 */
export interface ExportScope {
  label: string;
  scope: KnowledgeScope;
}

export interface ExportableMessage {
  role: "user" | "assistant" | "error" | "stopped";
  text: string;
  attributions?: CopilotAttribution[];
}

/**
 * The sources an answer actually rests on.
 *
 * Graph returns two kinds of attribution on a turn, and treating them alike
 * overstates what was used. `citation` is a source the answer was grounded in.
 * `annotation` is an entity Copilot recognized in passing — a person, a meeting,
 * a file mentioned in the text — linked out for convenience. Listing those as
 * "sources" claims the answer came from material that was only brushed against.
 *
 * Duplicates collapse by URL: the same document routinely arrives once per
 * passage quoted from it, and a reader wants the document listed once.
 */
export function usedSources(
  attributions: CopilotAttribution[] | undefined,
): CopilotAttribution[] {
  if (!attributions?.length) return [];
  const seen = new Set<string>();
  const used: CopilotAttribution[] = [];
  for (const attribution of attributions) {
    if (attribution.attributionType !== "citation") continue;
    const url = attribution.seeMoreWebUrl;
    if (!url || seen.has(url)) continue;
    seen.add(url);
    used.push(attribution);
  }
  return used;
}

/** `Rollout questions` → `copilot-rollout-questions-2026-09-08`. */
export function exportFileName(title: string, at = new Date()): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9֐-׿]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  const day = at.toISOString().slice(0, 10);
  return `copilot-${slug || "chat"}-${day}`;
}

function label(role: ExportableMessage["role"]): string {
  if (role === "user") return "You";
  if (role === "assistant") return "Copilot";
  return role === "stopped" ? "Stopped" : "Error";
}

/**
 * The transcript as Markdown — the answers are already Markdown, so this is the
 * format that survives being pasted into a ticket, a wiki or an email.
 */
export function toMarkdown(
  conversation: Pick<CopilotConversationSummary, "title" | "createdAt" | "lastActivityAt">,
  messages: ExportableMessage[],
  scope?: ExportScope | null,
): string {
  const lines = [
    `# ${conversation.title}`,
    "",
    `Microsoft 365 Copilot · started ${new Date(conversation.createdAt).toLocaleString()} · last activity ${new Date(conversation.lastActivityAt).toLocaleString()}`,
    "",
  ];

  if (scope) {
    // The scope of the LATEST turn, which is what the chat is currently aimed
    // at — a chat can be re-aimed between questions, and only the current aim
    // is stored against it.
    lines.push(`Knowledge scope · ${scope.label}${scopeDetail(scope.scope)}`, "");
  }

  for (const message of messages) {
    lines.push(`## ${label(message.role)}`, "", message.text.trim(), "");
    const sources = usedSources(message.attributions);
    if (sources.length > 0) {
      lines.push("Sources:");
      for (const source of sources) {
        lines.push(
          `- [${source.providerDisplayName || source.seeMoreWebUrl}](${source.seeMoreWebUrl})`,
        );
      }
      lines.push("");
    }
  }

  lines.push(
    "---",
    "",
    "Exported from Microsoft 365 Copilot. Copilot can make mistakes — verify important information.",
    "",
  );
  return lines.join("\n");
}

/**
 * The identifier the scope actually narrows on, when it has one.
 *
 * A folder called "Licenses" is not a location anyone can check; the path the
 * filter was built from is. Written next to the label so a transcript can be
 * used to work out why a scoped search found nothing.
 */
function scopeDetail(scope: KnowledgeScope): string {
  if (scope.kind === "sharePointPath" || scope.kind === "oneDrivePath") {
    return ` (${scope.path})`;
  }
  if (scope.kind === "sharePointSite") return ` (SiteID ${scope.siteId})`;
  return "";
}

/** The same transcript as JSON, for anything that needs to parse it. */
export function toJson(
  conversation: CopilotConversationSummary,
  messages: ExportableMessage[],
  scope?: ExportScope | null,
): string {
  return JSON.stringify(
    {
      exportedAt: new Date().toISOString(),
      source: "Microsoft 365 Copilot",
      conversation: {
        title: conversation.title,
        createdAt: conversation.createdAt,
        lastActivityAt: conversation.lastActivityAt,
        turnCount: conversation.turnCount,
        knowledgeScope: scope ? { label: scope.label, ...scope.scope } : null,
      },
      messages: messages.map((message) => ({
        role: message.role,
        text: message.text,
        sources: usedSources(message.attributions),
      })),
    },
    null,
    2,
  );
}

/**
 * Hands the file to the browser. Kept here so the page does not carry DOM
 * plumbing, and so the revoke happens on the same tick as the click.
 */
export function downloadFile(name: string, mime: string, contents: string): void {
  const url = URL.createObjectURL(new Blob([contents], { type: `${mime};charset=utf-8` }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
